import type { ActionPattern } from 'redux-saga/effects';
import { actionChannel, all, call, debounce, getContext, put, race, select, take, takeEvery, takeLatest } from 'redux-saga/effects';
import i18n from '../../../i18n';
import type { Action } from '../utils';
import type { ICustomerBase } from '../../api/domain/customer-base';
import type {
    BsiTask,
    IBsiAccountCategoryDeletionInput,
    IBsiAccountCategoryModificationInput,
    IBsiAccountCreationTask,
    IBsiAccountDeletionInput,
    IBsiAddressModificationInput,
    IBsiBusinessRelationshipConfirmationInput,
    IBsiCard,
    IBsiCardDeletionInput,
    IBsiCardOrderInput,
    IBsiCivilStatusModificationInput,
    IBsiEBankingContractCreationInput,
    IBsiEBankingContractDeletionInput,
    IBsiEmailModificationInput,
    IBsiNameModificationInput,
    IBsiPhoneModificationInput,
    IBsiPowerOfAttorneyCreationInput,
    IBsiPowerOfAttorneyDeletionInput,
    IBsiCreateNoInterestRequest,
    IProcessInstance,
    IProcessTaskRequest,
    IBsiPopupResponses,
    IBsiPopupResponseCreateRequest,
    IBsiPopupResponsesFetchRequest,
} from '../../api/domain/process-engine';
import type { IPostBsiCaseRequest } from '../../api/domain/process-engine/post-bsi-case-request';
import type { ProcessEngineApiService } from '../../api/services/process-engine/process-engine-api-service';
import type { IModifyCardLimitsFormValues } from '../../components/form/card-limits-form-fragment/card-limits-form-fragment';
import { NotificationType, ProcessEngineActivityId, ProcessEngineTask, RouteTranslationKey } from '../../enum';
import { applicationSelectors } from '../application';
import {
    addNotification,
    addNotificationClientAdvisory,
    changeLocation,
    hideProgressIndicator,
    showProgressIndicator,
} from '../application/application-actions';
import { customerBaseApiSelectors } from '../customer-base-api';
import { logError } from '../logging-api/logging-api-actions';
import {
    confirmBusinessRelationship,
    confirmBusinessRelationshipFailure,
    confirmBusinessRelationshipSuccess,
    createEBankingContract,
    createEBankingContractFailure,
    createEBankingContractSuccess,
    createNoInterest,
    createNoInterestFailure,
    createNoInterestSuccess,
    createPopupResponse,
    createPopupResponseFailure,
    createPopupResponseSuccess,
    createPowerOfAttorney,
    createPowerOfAttorneyFailure,
    createPowerOfAttorneySuccess,
    deleteAccount,
    deleteAccountFailure,
    deleteAccountSuccess,
    deleteCard,
    deleteCardFailure,
    deleteCardSuccess,
    deleteEBankingContract,
    deleteEBankingContractFailure,
    deleteEBankingContractSuccess,
    deletePowerOfAttorney,
    deletePowerOfAttorneyFailure,
    deletePowerOfAttorneySuccess,
    fetchAllInstances,
    fetchAllInstancesFailure,
    fetchAllInstancesSuccess,
    fetchAllPopupResponses,
    fetchBsiCases,
    fetchBsiCasesFailure,
    fetchBsiCasesSuccess,
    fetchInstance,
    fetchInstanceFailure,
    fetchInstanceSuccess,
    fetchPopupResponses,
    fetchPopupResponsesFailure,
    fetchPopupResponsesSuccess,
    markPopupNotificationForDeletion,
    modifyAccountCategory,
    modifyAccountCategoryFailure,
    modifyAccountCategorySuccess,
    modifyAddress,
    modifyAddressFailure,
    modifyAddressSuccess,
    modifyCardLimits,
    modifyCardLimitsFailure,
    modifyCardLimitsSuccess,
    modifyCivilStatus,
    modifyCivilStatusFailure,
    modifyCivilStatusSuccess,
    modifyEmail,
    modifyEmailFailure,
    modifyEmailSuccess,
    modifyName,
    modifyNameFailure,
    modifyNameSuccess,
    modifyPhone,
    modifyPhoneFailure,
    modifyPhoneSuccess,
    orderCard,
    orderCardFailure,
    orderCardSuccess,
    postBsiCase,
    postBsiCaseFailure,
    postBsiCaseSuccess,
    processTask,
    processTaskFailure,
    processTaskSuccess,
    startBsiProcess,
    startBsiProcessFailure,
    startBsiProcessSuccess,
    submitProductOrder,
    submitProductOrderFailure,
    submitProductOrderSuccess,
} from './process-engine-api-actions';
import de from './translations/de.json';
import en from './translations/en.json';
import fr from './translations/fr.json';
import it from './translations/it.json';
import type { IProductFormFormikProps } from '../../components/product/product-form';
import { hasPopupsPermission } from '../../utils/customer-base-permission-helper';

function* addTranslations() {
    i18n.addResources('de', de);
    i18n.addResources('en', en);
    i18n.addResources('fr', fr);
    i18n.addResources('it', it);

    yield;
}

function* handleSubmitProductOrder({
    payload: {
        notes,
        selectedFinnovaId: accountType,
        currency,
        creditCard,
        creditCardType,
        debitCard,
        debitCardType,
        cardHolder,
        isProspect,
        card,
        debitCardLimitDailyCard,
        debitCardLimitMonthlyGlobal,
        creditCardLimitDailyCard,
        creditCardLimitMonthlyGlobal,
        displayCardHolderNameInput,
        displayAccountHolderNameInput,
        debitCardHolderName,
        debitCardAccountHolderName,
        creditCardHolderName,
        creditCardAccountHolderName,
        productDescription,
    },
}: Action<IProductFormFormikProps>) {
    try {
        const selectedCustomerBaseForProducts: ICustomerBase = yield select(applicationSelectors.getSelectedCustomerBaseForProducts);
        const cards = card
            ? ([
                  debitCard
                      ? ({
                            cardType: debitCardType,
                            cardHolder,
                            cardLimitDailyCard: debitCardLimitDailyCard,
                            cardLimitMonthlyGlobal: debitCardLimitMonthlyGlobal,
                            cardHolderName: displayCardHolderNameInput ? debitCardHolderName : undefined,
                            accountHolderName: displayAccountHolderNameInput ? debitCardAccountHolderName : undefined,
                        } as IBsiCard)
                      : null,
                  creditCard
                      ? ({
                            cardType: creditCardType,
                            cardHolder,
                            cardLimitDailyCard: creditCardLimitDailyCard,
                            cardLimitMonthlyGlobal: creditCardLimitMonthlyGlobal,
                            cardHolderName: displayCardHolderNameInput ? creditCardHolderName : undefined,
                            accountHolderName: displayAccountHolderNameInput ? creditCardAccountHolderName : undefined,
                        } as IBsiCard)
                      : null,
              ].filter(Boolean) as IBsiCard[])
            : [];

        const commonBsiCaseInput: IBsiAccountCreationTask['BsiCaseInput'] = {
            accountType,
            currency,
            cards,
            notes,
        };

        const isFinnovaCode = (selection: string): boolean => {
            return parseInt(selection, 10).toString() === selection;
        };

        if (productDescription) {
            // send either of category, categoryCode, but not both
            if (isFinnovaCode(productDescription)) {
                commonBsiCaseInput['categoryCode'] = productDescription;
            } else {
                commonBsiCaseInput['category'] = productDescription;
            }
        }

        const orderProductTask: BsiTask = isProspect
            ? {
                  taskType: ProcessEngineTask.CREATE_ACCOUNT_PROSPECT,
                  BsiCaseInput: commonBsiCaseInput,
              }
            : {
                  taskType: ProcessEngineTask.CREATE_ACCOUNT,
                  BsiCaseInput: commonBsiCaseInput,
              };

        yield call(postBsiCaseAndConfirmSuccess, {
            bsiTask: orderProductTask,
            customerBase: selectedCustomerBaseForProducts,
        });
        yield put(submitProductOrderSuccess());
    } catch (error: unknown) {
        yield put(logError(`submitProductOrder failed with: ${error}`));
        yield put(submitProductOrderFailure());
    }
}

function* handleSubmitProductOrderSuccess() {
    yield put(
        changeLocation({
            location: i18n.t(RouteTranslationKey.PRODUCTS),
        }),
    );
    yield put(
        addNotification({
            title: i18n.t('submit-product-order.success.title'),
            text: i18n.t('submit-product-order.success.body'),
            type: NotificationType.SUCCESS,
        }),
    );
}

function* handleSubmitProductOrderFailure() {
    yield put(
        changeLocation({
            location: i18n.t(RouteTranslationKey.PRODUCTS),
        }),
    );
    yield put(
        addNotificationClientAdvisory({
            titleKey: 'submit-product-order.failure.title',
        }),
    );
}

function* handleOrderCard({ payload: BsiCaseInput }: Action<IBsiCardOrderInput>) {
    try {
        const selectedCustomerBaseForProducts: ICustomerBase = yield select(applicationSelectors.getSelectedCustomerBaseForProducts);
        const orderCardTask: BsiTask = {
            taskType: ProcessEngineTask.ORDER_CARD,
            BsiCaseInput,
        };
        yield call(postBsiCaseAndConfirmSuccess, {
            bsiTask: orderCardTask,
            customerBase: selectedCustomerBaseForProducts,
        });
        yield put(orderCardSuccess());
    } catch (error: unknown) {
        yield put(logError(`orderCard failed with: ${error}`));
        yield put(orderCardFailure());
    }
}

function* handleOrderCardSuccess() {
    yield put(
        addNotification({
            title: i18n.t('order-card.success.title'),
            text: i18n.t('order-card.success.body'),
            type: NotificationType.SUCCESS,
        }),
    );
}

function* handleOrderCardFailure() {
    yield put(
        addNotificationClientAdvisory({
            titleKey: 'order-card.failure.title',
        }),
    );
}

function* handleModifyAddress({ payload: BsiCaseInput }: Action<IBsiAddressModificationInput>) {
    try {
        const selectedCustomerBaseForEServices: ICustomerBase = yield select(applicationSelectors.getSelectedCustomerBaseForEServices);
        const domicileModificationTask: BsiTask = {
            taskType: ProcessEngineTask.MODIFY_ADDRESS,
            BsiCaseInput,
        };
        yield call(postBsiCaseAndConfirmSuccess, {
            bsiTask: domicileModificationTask,
            customerBase: selectedCustomerBaseForEServices,
        });
        yield put(modifyAddressSuccess());
    } catch (error: unknown) {
        yield put(logError(`modifyAddress failed with: ${error}`));
        yield put(modifyAddressFailure());
    }
}

function* handleModifyAddressSuccess() {
    yield put(
        addNotification({
            title: i18n.t('modify-domicile.success.title'),
            text: i18n.t('modify-domicile.success.body'),
            type: NotificationType.SUCCESS,
        }),
    );
}

function* handleModifyAddressFailure() {
    yield put(
        addNotificationClientAdvisory({
            titleKey: 'modify-domicile.failure.title',
        }),
    );
}

function* handleModifyCivilStatus({ payload: BsiCaseInput }: Action<IBsiCivilStatusModificationInput>) {
    try {
        const selectedCustomerBaseForEServices: ICustomerBase = yield select(applicationSelectors.getSelectedCustomerBaseForEServices);
        const civilStatusModificationTask: BsiTask = {
            taskType: ProcessEngineTask.MODIFY_CIVIL_STATUS,
            BsiCaseInput,
        };
        yield call(postBsiCaseAndConfirmSuccess, {
            bsiTask: civilStatusModificationTask,
            customerBase: selectedCustomerBaseForEServices,
        });
        yield put(modifyCivilStatusSuccess());
    } catch (error: unknown) {
        yield put(logError(`modifyCivilStatus failed with: ${error}`));
        yield put(modifyCivilStatusFailure());
    }
}

function* handleModifyCivilStatusSuccess() {
    yield put(
        addNotification({
            title: i18n.t('modify-civil-status.success.title'),
            text: i18n.t('modify-civil-status.success.body'),
            type: NotificationType.SUCCESS,
        }),
    );
}

function* handleModifyCivilStatusFailure() {
    yield put(
        addNotificationClientAdvisory({
            titleKey: 'modify-civil-status.failure.title',
        }),
    );
}

function* handleModifyAccountCategory({ payload: BsiCaseInput }: Action<IBsiAccountCategoryModificationInput>) {
    try {
        const selectedCustomerBaseForProducts: ICustomerBase = yield select(applicationSelectors.getSelectedCustomerBaseForProducts);

        const isFinnovaCode = (selection: string): boolean => {
            return parseInt(selection, 10).toString() === selection;
        };

        // trigger dedicated process engine task for deletion when the category is empty
        const AccountCategoryModificationTask: BsiTask = BsiCaseInput.category
            ? {
                  taskType: ProcessEngineTask.MODIFY_ACCOUNT_CATEGORY,
                  BsiCaseInput: { ...BsiCaseInput, categoryCode: isFinnovaCode(BsiCaseInput.category) ? BsiCaseInput.category : undefined },
              }
            : {
                  taskType: ProcessEngineTask.DELETE_ACCOUNT_CATEGORY,
                  BsiCaseInput: { ...BsiCaseInput, category: undefined } as IBsiAccountCategoryDeletionInput,
              };

        yield call(postBsiCaseAndConfirmSuccess, {
            bsiTask: AccountCategoryModificationTask,
            customerBase: selectedCustomerBaseForProducts,
        });

        yield put(modifyAccountCategorySuccess());
    } catch (error: unknown) {
        yield put(logError(`modifyAccountCategory failed with: ${error}`));
        yield put(modifyAccountCategoryFailure());
    }
}

function* handleModifyAccountCategorySuccess() {
    yield put(
        addNotification({
            title: i18n.t('modify-account-category.success.title'),
            text: i18n.t('modify-account-category.success.body'),
            type: NotificationType.SUCCESS,
        }),
    );
}

function* handleModifyAccountCategoryFailure() {
    yield put(
        addNotificationClientAdvisory({
            titleKey: 'modify-account-category.failure.title',
        }),
    );
}

function* handleModifyEmail({ payload }: Action<IBsiEmailModificationInput>) {
    try {
        const selectedCustomerBaseForEServices: ICustomerBase = yield select(applicationSelectors.getSelectedCustomerBaseForEServices);
        const emailModificationTask: BsiTask = {
            taskType: payload.email ? ProcessEngineTask.MODIFY_EMAIL : ProcessEngineTask.DELETE_EMAIL,
            BsiCaseInput: payload,
        };
        yield call(postBsiCaseAndConfirmSuccess, {
            bsiTask: emailModificationTask,
            customerBase: selectedCustomerBaseForEServices,
        });
        yield put(modifyEmailSuccess());
    } catch (error: unknown) {
        yield put(logError(`modifyEmail failed with: ${error}`));
        yield put(modifyEmailFailure());
    }
}

function* handleModifyEmailSuccess() {
    yield put(
        addNotification({
            title: i18n.t('modify-email.success.title'),
            text: i18n.t('modify-email.success.body'),
            type: NotificationType.SUCCESS,
        }),
    );
}

function* handleModifyEmailFailure() {
    yield put(
        addNotificationClientAdvisory({
            titleKey: 'modify-email.failure.title',
        }),
    );
}

function* handleModifyPhone({ payload }: Action<IBsiPhoneModificationInput>) {
    try {
        const selectedCustomerBaseForEServices: ICustomerBase = yield select(applicationSelectors.getSelectedCustomerBaseForEServices);
        const phoneModificationTask: BsiTask = {
            taskType: payload.phone ? ProcessEngineTask.MODIFY_PHONE : ProcessEngineTask.DELETE_PHONE,
            BsiCaseInput: payload,
        };

        yield call(postBsiCaseAndConfirmSuccess, {
            bsiTask: phoneModificationTask,
            customerBase: selectedCustomerBaseForEServices,
        });
        yield put(modifyPhoneSuccess());
    } catch (error: unknown) {
        yield put(logError(`modifyPhone failed with: ${error}`));
        yield put(modifyPhoneFailure());
    }
}

function* handleModifyPhoneSuccess() {
    yield put(
        addNotification({
            title: i18n.t('modify-phone.success.title'),
            text: i18n.t('modify-phone.success.body'),
            type: NotificationType.SUCCESS,
        }),
    );
}

function* handleModifyPhoneFailure() {
    yield put(
        addNotificationClientAdvisory({
            titleKey: 'modify-phone.failure.title',
        }),
    );
}

function* handleModifyName({ payload: BsiCaseInput }: Action<IBsiNameModificationInput>) {
    try {
        const selectedCustomerBaseForEServices: ICustomerBase = yield select(applicationSelectors.getSelectedCustomerBaseForEServices);
        const nameModificationTask: BsiTask = {
            taskType: ProcessEngineTask.MODIFY_NAME,
            BsiCaseInput,
        };
        yield call(postBsiCaseAndConfirmSuccess, {
            bsiTask: nameModificationTask,
            customerBase: selectedCustomerBaseForEServices,
        });
        yield put(modifyNameSuccess());
    } catch (error: unknown) {
        yield put(logError(`modifyName failed with: ${error}`));
        yield put(modifyNameFailure());
    }
}

function* handleModifyNameSuccess() {
    yield put(
        addNotification({
            title: i18n.t('modify-name.success.title'),
            text: i18n.t('modify-name.success.body'),
            type: NotificationType.SUCCESS,
        }),
    );
}

function* handleModifyNameFailure() {
    yield put(
        addNotificationClientAdvisory({
            titleKey: 'modify-name.failure.title',
        }),
    );
}

function* handleDeleteCard({ payload: BsiCaseInput }: Action<IBsiCardDeletionInput>) {
    try {
        const selectedCustomerBaseForProducts: ICustomerBase = yield select(applicationSelectors.getSelectedCustomerBaseForProducts);
        const deleteCardTask: BsiTask = {
            taskType: ProcessEngineTask.DELETE_CARD,
            BsiCaseInput,
        };
        yield call(postBsiCaseAndConfirmSuccess, {
            bsiTask: deleteCardTask,
            customerBase: selectedCustomerBaseForProducts,
        });
        yield put(deleteCardSuccess());
    } catch (error: unknown) {
        yield put(logError(`deleteCard failed with: ${error}`));
        yield put(deleteCardFailure());
    }
}

function* handleDeleteCardSuccess() {
    yield put(
        addNotification({
            title: i18n.t('delete-card.success.title'),
            text: i18n.t('delete-card.success.body'),
            type: NotificationType.SUCCESS,
        }),
    );
}

function* handleDeleteCardFailure() {
    yield put(
        addNotificationClientAdvisory({
            titleKey: 'delete-card.failure.title',
        }),
    );
}

function* handleDeleteAccount({ payload: BsiCaseInput }: Action<IBsiAccountDeletionInput>) {
    try {
        const selectedCustomerBaseForProducts: ICustomerBase = yield select(applicationSelectors.getSelectedCustomerBaseForProducts);
        const deleteAccountTask: BsiTask = {
            taskType: ProcessEngineTask.DELETE_ACCOUNT,
            BsiCaseInput,
        };
        yield call(postBsiCaseAndConfirmSuccess, {
            bsiTask: deleteAccountTask,
            customerBase: selectedCustomerBaseForProducts,
        });
        yield put(deleteAccountSuccess());
    } catch (error: unknown) {
        yield put(logError(`deleteAccount failed with: ${error}`));
        yield put(deleteAccountFailure());
    }
}

function* handleDeleteAccountSuccess() {
    yield put(
        addNotification({
            title: i18n.t('delete-account.success.title'),
            text: i18n.t('delete-account.success.body'),
            type: NotificationType.SUCCESS,
        }),
    );
}

function* handleDeleteAccountFailure() {
    yield put(
        addNotificationClientAdvisory({
            titleKey: 'delete-account.failure.title',
        }),
    );
}

function* handleDeleteEBankingContract({ payload: BsiCaseInput }: Action<IBsiEBankingContractDeletionInput>) {
    try {
        const selectedCustomerBaseForProducts: ICustomerBase = yield select(applicationSelectors.getSelectedCustomerBaseForEServices);
        const deleteEBankingContractTask: BsiTask = {
            taskType: ProcessEngineTask.DELETE_EBANKING_CONTRACT,
            BsiCaseInput,
        };
        yield call(postBsiCaseAndConfirmSuccess, {
            bsiTask: deleteEBankingContractTask,
            customerBase: selectedCustomerBaseForProducts,
        });
        yield put(deleteEBankingContractSuccess());
    } catch (error: unknown) {
        yield put(logError(`deleteEBankingContract failed with: ${error}`));
        yield put(deleteEBankingContractFailure());
    }
}

function* handleDeleteEBankingContractSuccess() {
    yield put(
        addNotification({
            title: i18n.t('delete-e-banking-contract.success.title'),
            text: i18n.t('delete-e-banking-contract.success.body'),
            type: NotificationType.SUCCESS,
        }),
    );
}

function* handleDeleteEBankingContractFailure() {
    yield put(
        addNotificationClientAdvisory({
            titleKey: 'delete-e-banking-contract.failure.title',
        }),
    );
}

function* handleCreateEBankingContract({ payload: BsiCaseInput }: Action<IBsiEBankingContractCreationInput>) {
    try {
        const selectedCustomerBaseForProducts: ICustomerBase = yield select(applicationSelectors.getSelectedCustomerBaseForEServices);
        const createEBankingContractTask: BsiTask = {
            taskType: ProcessEngineTask.CREATE_EBANKING_CONTRACT,
            BsiCaseInput,
        };
        yield call(postBsiCaseAndConfirmSuccess, {
            bsiTask: createEBankingContractTask,
            customerBase: selectedCustomerBaseForProducts,
        });
        yield put(createEBankingContractSuccess());
    } catch (error: unknown) {
        yield put(logError(`createEBankingContract failed with: ${error}`));
        yield put(createEBankingContractFailure());
    }
}

function* handleCreateEBankingContractSuccess() {
    yield put(
        addNotification({
            title: i18n.t('create-e-banking-contract.success.title'),
            text: i18n.t('create-e-banking-contract.success.body'),
            type: NotificationType.SUCCESS,
        }),
    );
}

function* handleCreateEBankingContractFailure() {
    yield put(
        addNotificationClientAdvisory({
            titleKey: 'create-e-banking-contract.failure.title',
        }),
    );
}

function* handleDeletePowerOfAttorney({ payload: BsiCaseInput }: Action<IBsiPowerOfAttorneyDeletionInput>) {
    try {
        const selectedCustomerBaseForProducts: ICustomerBase = yield select(applicationSelectors.getSelectedCustomerBaseForEServices);
        const deletePowerOfAttorneyTask: BsiTask = {
            taskType: ProcessEngineTask.DELETE_POWER_OF_ATTORNEY,
            BsiCaseInput,
        };
        yield call(postBsiCaseAndConfirmSuccess, {
            bsiTask: deletePowerOfAttorneyTask,
            customerBase: selectedCustomerBaseForProducts,
        });
        yield put(deletePowerOfAttorneySuccess());
    } catch (error: unknown) {
        yield put(logError(`deletePowerOfAttorney failed with: ${error}`));
        yield put(deletePowerOfAttorneyFailure());
    }
}

function* handleDeletePowerOfAttorneySuccess() {
    yield put(
        addNotification({
            title: i18n.t('delete-power-of-attorney.success.title'),
            text: i18n.t('delete-power-of-attorney.success.body'),
            type: NotificationType.SUCCESS,
        }),
    );
}

function* handleDeletePowerOfAttorneyFailure() {
    yield put(
        addNotificationClientAdvisory({
            titleKey: 'delete-power-of-attorney.failure.title',
        }),
    );
}

function* handleCreatePowerOfAttorney({ payload: BsiCaseInput }: Action<IBsiPowerOfAttorneyCreationInput>) {
    try {
        const selectedCustomerBaseForProducts: ICustomerBase = yield select(applicationSelectors.getSelectedCustomerBaseForEServices);
        const createPowerOfAttorneyTask: BsiTask = {
            taskType: ProcessEngineTask.CREATE_POWER_OF_ATTORNEY,
            BsiCaseInput,
        };
        yield call(postBsiCaseAndConfirmSuccess, {
            bsiTask: createPowerOfAttorneyTask,
            customerBase: selectedCustomerBaseForProducts,
        });
        yield put(createPowerOfAttorneySuccess());
    } catch (error: unknown) {
        yield put(logError(`createPowerOfAttorney failed with: ${error}`));
        yield put(createPowerOfAttorneyFailure());
    }
}

function* handleCreatePowerOfAttorneySuccess() {
    yield put(
        addNotification({
            title: i18n.t('create-power-of-attorney.success.title'),
            text: i18n.t('create-power-of-attorney.success.body'),
            type: NotificationType.SUCCESS,
        }),
    );
}

function* handleCreatePowerOfAttorneyFailure() {
    yield put(
        addNotificationClientAdvisory({
            titleKey: 'create-power-of-attorney.failure.title',
        }),
    );
}

function* postBsiCaseAndConfirmSuccess(request: IPostBsiCaseRequest) {
    yield put(postBsiCase(request));
    const { postBsiCaseSuccessAction }: { postBsiCaseSuccessAction?: Action<void> } = yield race({
        postBsiCaseSuccessAction: take(postBsiCaseSuccess),
        postBsiCaseFailureAction: take(postBsiCaseFailure),
    });
    if (!postBsiCaseSuccessAction) {
        throw new Error(`One of the tasks failed: ${JSON.stringify(request.bsiTask)}`);
    }
}

function* handleModifyCardLimits({ payload: { notes, cardNr, cardLimitDailyCard, cardLimitMonthlyGlobal } }: Action<IModifyCardLimitsFormValues>) {
    try {
        const selectedCustomerBaseForProducts: ICustomerBase = yield select(applicationSelectors.getSelectedCustomerBaseForProducts);
        const modifyCardLimitsTask: BsiTask = {
            taskType: ProcessEngineTask.MODIFY_CARD_LIMIT,
            BsiCaseInput: {
                cardLimitDaily: cardLimitDailyCard !== undefined ? `${cardLimitDailyCard}` : undefined,
                cardLimitMonthly: cardLimitMonthlyGlobal !== undefined ? `${cardLimitMonthlyGlobal}` : undefined,
                cardLimitDailyCard,
                cardLimitMonthlyGlobal,
                cardNr,
                notes,
            },
        };
        yield call(postBsiCaseAndConfirmSuccess, {
            bsiTask: modifyCardLimitsTask,
            customerBase: selectedCustomerBaseForProducts,
        });
        yield put(modifyCardLimitsSuccess());
    } catch (error: unknown) {
        yield put(logError(`modifyCardLimit failed with: ${error}`));
        yield put(modifyCardLimitsFailure());
    }
}

function* handleModifyCardLimitsSuccess() {
    yield put(
        addNotification({
            title: i18n.t('modify-card-limit.success.title'),
            text: i18n.t('modify-card-limit.success.body'),
            type: NotificationType.SUCCESS,
        }),
    );
}

function* handleModifyCardLimitsFailure() {
    yield put(
        addNotificationClientAdvisory({
            titleKey: 'modify-card-limit.failure.title',
        }),
    );
}

function* handleConfirmBusinessRelationship({ payload: BsiCaseInput }: Action<IBsiBusinessRelationshipConfirmationInput>) {
    try {
        const selectedCustomerBaseForProducts: ICustomerBase = yield select(applicationSelectors.getSelectedCustomerBaseForEServices);
        const confirmBusinessRelationshipLimitTask: BsiTask = {
            taskType: ProcessEngineTask.CONFIRM_BUSINESS_RELATIONSHIP,
            BsiCaseInput,
        };
        yield call(postBsiCaseAndConfirmSuccess, {
            bsiTask: confirmBusinessRelationshipLimitTask,
            customerBase: selectedCustomerBaseForProducts,
        });
        yield put(confirmBusinessRelationshipSuccess());
    } catch (error: unknown) {
        yield put(logError(`confirmBusinessRelationship failed with: ${error}`));
        yield put(confirmBusinessRelationshipFailure());
    }
}

function* handleConfirmBusinessRelationshipSuccess() {
    yield put(
        addNotification({
            title: i18n.t('confirm-business-relationship.success.title'),
            text: i18n.t('confirm-business-relationship.success.body'),
            type: NotificationType.SUCCESS,
        }),
    );
}

function* handleConfirmBusinessRelationshipFailure() {
    yield put(
        addNotificationClientAdvisory({
            titleKey: 'confirm-business-relationship.failure.title',
        }),
    );
}

const isBsiTask = (toBeChecked: any): toBeChecked is BsiTask => toBeChecked['taskType'] !== undefined && toBeChecked['BsiCaseInput'] !== undefined;

function* handleFetchBsiCases() {
    try {
        yield put(fetchAllInstances());
        const { fetchAllInstancesSuccessAction }: { fetchAllInstancesSuccessAction?: Action<IProcessInstance[]> } = yield race({
            fetchAllInstancesSuccessAction: take(fetchAllInstancesSuccess),
            fetchAllInstancesFailureAction: take(fetchAllInstancesFailure),
        });
        if (!fetchAllInstancesSuccessAction) {
            throw new Error('could not fetch process instances');
        }
        const bsiCases: BsiTask[] = fetchAllInstancesSuccessAction.payload.filter((instance) => isBsiTask(instance.vars)).map((instance) => instance.vars);
        yield put(fetchBsiCasesSuccess(bsiCases));
    } catch (error: unknown) {
        yield put(logError(`fetch bsi cases failed with: ${error}`));
        yield put(fetchBsiCasesFailure());
    }
}

function* watchPostBsiCase() {
    const postBsiActionChannel: ActionPattern = yield actionChannel(postBsiCase);
    while (true) {
        const postBsiAction: Action<IPostBsiCaseRequest> = yield take(postBsiActionChannel);
        yield call(handlePostBsiCase, postBsiAction);
    }
}

function* handlePostBsiCase({ payload: { bsiTask, customerBase } }: Action<IPostBsiCaseRequest>) {
    yield put(showProgressIndicator());
    try {
        yield put(startBsiProcess(customerBase));

        const { startBsiProcessSuccessAction }: { startBsiProcessSuccessAction?: Action<string> } = yield race({
            startBsiProcessSuccessAction: take(startBsiProcessSuccess),
            startBsiProcessFailureAction: take(startBsiProcessFailure),
        });
        if (!startBsiProcessSuccessAction) {
            throw new Error('Cant initiate bsi process');
        }

        yield put(fetchInstance(startBsiProcessSuccessAction.payload));
        const { fetchInstanceSuccessAction }: { fetchInstanceSuccessAction?: Action<IProcessInstance> } = yield race({
            fetchInstanceSuccessAction: take(fetchInstanceSuccess),
            fetchInstanceFailureAction: take(fetchInstanceFailure),
        });
        if (!fetchInstanceSuccessAction) {
            throw new Error('Cant fetch process instance');
        }

        const postBsiCaseTask = fetchInstanceSuccessAction.payload.tasks.find((task) => task.activityId === ProcessEngineActivityId.POST_BSI_CASE);
        const bsiCallbackTask = fetchInstanceSuccessAction.payload.tasks.find((task) => task.activityId === ProcessEngineActivityId.BSI_CALLBACK);
        if (!postBsiCaseTask || !bsiCallbackTask) {
            throw new Error(`The fetched process instance does not contain all required activities.
            Must include ${ProcessEngineActivityId.POST_BSI_CASE} and ${ProcessEngineActivityId.BSI_CALLBACK}.
            It only contains activityIds: ${fetchInstanceSuccessAction.payload.tasks.map((task) => task.activityId).join(',')}`);
        }

        const processTaskRequest: IProcessTaskRequest = {
            taskId: postBsiCaseTask.id,
            task: {
                ...bsiTask,
                id: bsiCallbackTask.id,
            },
        };
        yield put(processTask(processTaskRequest));
        const { processTaskSuccessAction } = yield race({
            processTaskSuccessAction: take(processTaskSuccess),
            processTaskFailureAction: take(processTaskFailure),
        });
        if (!processTaskSuccessAction) {
            throw new Error(`Could not process task: ${JSON.stringify(processTaskRequest)}`);
        }
        yield put(postBsiCaseSuccess());
    } catch (error: unknown) {
        yield put(logError(`postBsiCase failed with: ${error}`));
        yield put(postBsiCaseFailure());
    } finally {
        yield put(hideProgressIndicator());
    }
}

function* handleProcessTask({ payload: processTaskRequest }: Action<IProcessTaskRequest>) {
    const api: ProcessEngineApiService = yield getContext('processEngineApiService');
    try {
        yield call([api, api.processTask], processTaskRequest);
        yield put(processTaskSuccess());
    } catch (error: unknown) {
        yield put(logError(`process task failed with: ${error}`));
        yield put(processTaskFailure());
    }
}

function* handleStartBsiProcess({ payload }: Action<ICustomerBase | undefined>) {
    const api: ProcessEngineApiService = yield getContext('processEngineApiService');
    try {
        const ownCustomerBase: ICustomerBase | undefined = yield select(customerBaseApiSelectors.getOwnCustomerBase);
        const customerBase = payload || ownCustomerBase;
        const instanceId: string = yield call([api, api.startBsiProcess], customerBase?.externalId || '');
        yield put(startBsiProcessSuccess(instanceId));
    } catch (error: unknown) {
        yield put(logError(`start process failed with: ${error}`));
        yield put(startBsiProcessFailure());
    }
}

function* handleFetchInstance({ payload: instanceId }: Action<string>) {
    const api: ProcessEngineApiService = yield getContext('processEngineApiService');
    try {
        const instance: IProcessInstance = yield call([api, api.getInstance], instanceId);
        yield put(fetchInstanceSuccess(instance));
    } catch (error: unknown) {
        yield put(logError(`fetch instance failed with: ${error}`));
        yield put(fetchInstanceFailure());
    }
}

function* handleFetchAllInstances() {
    const api: ProcessEngineApiService = yield getContext('processEngineApiService');
    try {
        const instances: IProcessInstance[] = yield call([api, api.getAllInstances]);
        yield put(fetchAllInstancesSuccess(instances));
    } catch (error: unknown) {
        yield put(logError(`fetch all instances failed with: ${error}`));
        yield put(fetchAllInstancesFailure());
    }
}

function* handlePostBsiCaseSuccess() {
    yield put(fetchBsiCases());
}

function* handleCreateNoInterest({ payload: createNoInterestRequest }: Action<IBsiCreateNoInterestRequest>) {
    try {
        const selectedCustomerBaseForProducts: ICustomerBase = yield select(applicationSelectors.getSelectedCustomerBaseForProducts);
        const createNoInterestTask: BsiTask = {
            taskType: ProcessEngineTask.CREATE_NO_INTEREST,
            BsiCaseInput: createNoInterestRequest,
        };
        yield call(postBsiCaseAndConfirmSuccess, {
            bsiTask: createNoInterestTask,
            customerBase: selectedCustomerBaseForProducts,
        });
        yield put(createNoInterestSuccess());
    } catch (error: unknown) {
        yield put(logError(`create no interest failed with: ${error}`));
        yield put(createNoInterestFailure());
    }
}

function* handleCreateNoInterestSuccess() {
    yield put(
        addNotification({
            title: i18n.t('create-no-interest.success.title'),
            text: i18n.t('create-no-interest.success.body'),
            type: NotificationType.SUCCESS,
        }),
    );
}

function* handleCreateNoInterestFailure() {
    yield put(
        addNotificationClientAdvisory({
            titleKey: 'create-no-interest.failure.title',
        }),
    );
}

function* handleFetchAllPopupResponses() {
    const orderedCustomerBases: ICustomerBase[] = yield select(customerBaseApiSelectors.getOrderedCustomerBases);
    const isStrongAuthenticated: boolean = yield select(applicationSelectors.isStrongAuthenticated);
    if (isStrongAuthenticated && orderedCustomerBases) {
        for (const customerBase of orderedCustomerBases) {
            if (hasPopupsPermission(customerBase)) {
                yield put(fetchPopupResponses({ kdLnr: customerBase.externalId }));
            }
        }
    }
}

function* handleFetchPopupResponses({ payload }: Action<IBsiPopupResponsesFetchRequest>) {
    const processEngineApi: ProcessEngineApiService = yield getContext('processEngineApiService');
    try {
        const popupResponses: IBsiPopupResponses = yield call([processEngineApi, processEngineApi.getPopupResponses], payload);
        yield put(fetchPopupResponsesSuccess({ kdLnr: payload.kdLnr, popupResponses }));
    } catch (error: unknown) {
        yield put(logError(`fetch popup responses failed with: ${error}`));
        yield put(fetchPopupResponsesFailure());
    }
}

function* handleFetchPopupResponsesFailure() {
    yield put(
        addNotificationClientAdvisory({
            titleKey: 'popup-responses-fetch.failure.title',
        }),
    );
}

function* handleCreatePopupResponse({ payload }: Action<IBsiPopupResponseCreateRequest>) {
    const api: ProcessEngineApiService = yield getContext('processEngineApiService');
    try {
        yield call([api, api.postPopupResponse], payload);
        yield put(createPopupResponseSuccess());
        yield put(markPopupNotificationForDeletion({ kdLnr: payload.kdLnr, popupId: payload.popupId }));
    } catch (error: unknown) {
        yield put(logError(`post popup response failed with: ${error}`));
        yield put(createPopupResponseFailure());
    }
}

function* handleCreatePopupResponseFailure() {
    yield put(
        addNotificationClientAdvisory({
            titleKey: 'popup-response-create.failure.title',
        }),
    );
}

export function* processEngineApiSaga() {
    yield all([
        call(addTranslations),
        call(watchPostBsiCase),
        debounce(500, modifyEmailFailure, handleModifyEmailFailure),
        debounce(500, modifyEmailSuccess, handleModifyEmailSuccess),
        debounce(500, modifyPhoneFailure, handleModifyPhoneFailure),
        debounce(500, modifyPhoneSuccess, handleModifyPhoneSuccess),
        takeEvery(confirmBusinessRelationship, handleConfirmBusinessRelationship),
        takeEvery(confirmBusinessRelationshipFailure, handleConfirmBusinessRelationshipFailure),
        takeEvery(confirmBusinessRelationshipSuccess, handleConfirmBusinessRelationshipSuccess),
        takeEvery(createEBankingContract, handleCreateEBankingContract),
        takeEvery(createEBankingContractFailure, handleCreateEBankingContractFailure),
        takeEvery(createEBankingContractSuccess, handleCreateEBankingContractSuccess),
        takeEvery(createNoInterest, handleCreateNoInterest),
        takeEvery(createNoInterestFailure, handleCreateNoInterestFailure),
        takeEvery(createNoInterestSuccess, handleCreateNoInterestSuccess),
        takeEvery(createPowerOfAttorney, handleCreatePowerOfAttorney),
        takeEvery(createPowerOfAttorneyFailure, handleCreatePowerOfAttorneyFailure),
        takeEvery(createPowerOfAttorneySuccess, handleCreatePowerOfAttorneySuccess),
        takeEvery(deleteAccount, handleDeleteAccount),
        takeEvery(deleteAccountFailure, handleDeleteAccountFailure),
        takeEvery(deleteAccountSuccess, handleDeleteAccountSuccess),
        takeEvery(deleteCard, handleDeleteCard),
        takeEvery(deleteCardFailure, handleDeleteCardFailure),
        takeEvery(deleteCardSuccess, handleDeleteCardSuccess),
        takeEvery(deleteEBankingContract, handleDeleteEBankingContract),
        takeEvery(deleteEBankingContractFailure, handleDeleteEBankingContractFailure),
        takeEvery(deleteEBankingContractSuccess, handleDeleteEBankingContractSuccess),
        takeEvery(deletePowerOfAttorney, handleDeletePowerOfAttorney),
        takeEvery(deletePowerOfAttorneyFailure, handleDeletePowerOfAttorneyFailure),
        takeEvery(deletePowerOfAttorneySuccess, handleDeletePowerOfAttorneySuccess),
        takeEvery(fetchAllInstances, handleFetchAllInstances),
        takeEvery(fetchInstance, handleFetchInstance),
        takeEvery(modifyAccountCategory, handleModifyAccountCategory),
        takeEvery(modifyAccountCategoryFailure, handleModifyAccountCategoryFailure),
        takeEvery(modifyAccountCategorySuccess, handleModifyAccountCategorySuccess),
        takeEvery(modifyAddress, handleModifyAddress),
        takeEvery(modifyAddressFailure, handleModifyAddressFailure),
        takeEvery(modifyAddressSuccess, handleModifyAddressSuccess),
        takeEvery(modifyCardLimits, handleModifyCardLimits),
        takeEvery(modifyCardLimitsFailure, handleModifyCardLimitsFailure),
        takeEvery(modifyCardLimitsSuccess, handleModifyCardLimitsSuccess),
        takeEvery(modifyCivilStatus, handleModifyCivilStatus),
        takeEvery(modifyCivilStatusFailure, handleModifyCivilStatusFailure),
        takeEvery(modifyCivilStatusSuccess, handleModifyCivilStatusSuccess),
        takeEvery(modifyEmail, handleModifyEmail),
        takeEvery(modifyName, handleModifyName),
        takeEvery(modifyNameFailure, handleModifyNameFailure),
        takeEvery(modifyNameSuccess, handleModifyNameSuccess),
        takeEvery(modifyPhone, handleModifyPhone),
        takeEvery(orderCard, handleOrderCard),
        takeEvery(orderCardFailure, handleOrderCardFailure),
        takeEvery(orderCardSuccess, handleOrderCardSuccess),
        takeEvery(postBsiCaseSuccess, handlePostBsiCaseSuccess),
        takeEvery(processTask, handleProcessTask),
        takeEvery(startBsiProcess, handleStartBsiProcess),
        takeEvery(submitProductOrder, handleSubmitProductOrder),
        takeEvery(submitProductOrderFailure, handleSubmitProductOrderFailure),
        takeEvery(submitProductOrderSuccess, handleSubmitProductOrderSuccess),
        takeLatest(fetchBsiCases, handleFetchBsiCases),
        takeEvery(fetchAllPopupResponses, handleFetchAllPopupResponses),
        takeEvery(fetchPopupResponses, handleFetchPopupResponses),
        takeEvery(fetchPopupResponsesFailure, handleFetchPopupResponsesFailure),
        takeEvery(createPopupResponse, handleCreatePopupResponse),
        takeEvery(createPopupResponseFailure, handleCreatePopupResponseFailure),
    ]);
}
